import * as angular from 'angular';
import {IController, IPromise, IQService} from "angular";
import * as _ from "lodash";
import {AccountDialogService} from "../../services/account-dialogs-service.service";
import {AccountsAlerts} from "../../services/accounts-alerts.service";
import {BringozLoaderService} from "../../../commons/services/bringoz-loader.service";
import {BatchDialogService} from "../../../batch/services/batch-dialogs-service.service";
import {BatchRequestsService} from "../../../batch/services/batch-requests.service";
import {AccountModel} from "../../angular/models/account.model";
import {ConfirmChangeStateService} from "../../../commons/services/confirm-change-state.service";
import {ConfirmDialogService} from "../../../commons/services/confirm-dialog.service";
import {SplitService} from "../../../commons/services/split-service.service";

// Server SchemaEntityType of the accounts importer. Schema endpoints need import.schemas
// (IamImportPermission.SCHEMAS), while validate / import need import.accounts.
const ACCOUNTS_SCHEMA_ENTITY_TYPE = 'Account';
const ACCOUNTS_SCHEMA_PERMISSION = 'import.schemas';

export class uploadAccountsViewCtrl implements IController {

  public schemaEntityType: string = ACCOUNTS_SCHEMA_ENTITY_TYPE;
  public schemaPermissions: string[] = [ACCOUNTS_SCHEMA_PERMISSION];
  public schemas: any[] = [];
  public schemaDefaultFields: any[] = [];
  public isFieldMappingOpen: boolean = false;

  private schemasRequest: IPromise<void>;

  private readonly siteId: any;
  private accounts: AccountModel[];
  private errorAccounts: any;
  private warningAccounts: any;
  private currentNavItem: any;
  private editingAccount: any;
  private totals: any;
  private selectedIds: any;
  private queryString: any;
  private doUpdate: any;
  private uploadComplete: any;

  /* @ngInject */
  constructor(private accountDialogService: AccountDialogService, private batchDialogService: BatchDialogService, private accountsAlerts: AccountsAlerts,
              private confirmDialogService: ConfirmDialogService, private confirmChangeStateService: ConfirmChangeStateService, private accountsConstants,
              private splitService: SplitService, private $log, private UUIDService, private batchRequestsService: BatchRequestsService, private bringozLoaderService: BringozLoaderService, private $q: IQService,
              private IAMPermissions) {
    this.siteId = this.splitService.getSiteId();
  }

  $onInit() {
    this.doUpdate = false;
    this.uploadComplete = false;
    this.currentNavItem = 'total';
    this.selectedIds = [];
    this.accounts = [];
    this.errorAccounts = [];
    this.warningAccounts = [];
    this.totals = {};
    this.schemasRequest = this._loadSchemas();
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  changeNavItem(item) {
    this.currentNavItem = item;

    this.editingAccount = undefined;
    this.errorAccounts = [];
    this.warningAccounts = [];
    this.selectedIds = [];

    if (item === 'total') {
      this.accounts = this._resetAccounts(this.accounts, this._getErrorAccounts(this.accounts), this._getWarningAccounts(this.accounts));
    }
    if (item === 'errors') {
      this.errorAccounts = this._getErrorAccounts(this.accounts);
    }
    if (item === 'warnings') {
      this.warningAccounts = this._getWarningAccounts(this.accounts);
    }
  }

  onUpload(event) {
    // Wait for the schemas, else a quick click opens the dialog with an empty schema list.
    this.schemasRequest
      .then(() => this.accountDialogService.importAccounts(event, this.schemas))
      .then((accounts: AccountModel[]) => {
        accounts = this._setAllAccountsIds(accounts);
        this.accounts = this.accounts.concat(accounts);
        if (this.currentNavItem === 'errors') {
          this.errorAccounts = this._getErrorAccounts(this.accounts);
        }
        if (this.currentNavItem === 'warnings') {
          this.warningAccounts = this._getWarningAccounts(this.accounts);
        }
        this.totals = this._getTotals(this.accounts);
        this.uploadComplete = true;
        this.confirmChangeStateService.setIsEnabled(true);
      })
      .catch((error) => {
        if (error) {
          this.$log.error('Failed to upload file', error);
        } else {
          this.$log.info('Dialog Closed')
        }
      })
  }

  onQueryChange(query) {
    this.queryString = query;
  }

  onSelectedIdsChanged(selectedIds) {
    this.selectedIds = selectedIds;
  }

  onRemoveAccounts(ids) {
    this.accounts = _.filter(this.accounts, (account: AccountModel) => {
      return !_.includes(ids, account.accountId);
    });
    this.warningAccounts = _.filter(this.warningAccounts, (account: AccountModel) => {
      return !_.includes(ids, account.accountId);
    });
    this.errorAccounts = _.filter(this.errorAccounts, (account: AccountModel) => {
      return !_.includes(ids, account.accountId);
    });
    this.totals = this._getTotals(this.accounts);
    if (!this.accounts.length) {
      this.uploadComplete = false;
    }
  }

  onRemoveErrorAccounts(ids) {
    this.errorAccounts = _.filter(this.errorAccounts, (account: AccountModel) => {
      return !_.includes(ids, account.accountId);
    });
    const accounts = _.filter(this.accounts, (account: AccountModel) => {
      return !_.includes(ids, account.accountId);
    });
    this.accounts = this._resetAccounts(accounts, this.errorAccounts, this._getWarningAccounts(this.accounts));
    this.totals = this._getTotals(this.accounts);
    if (!this.accounts.length) {
      this.uploadComplete = false;
    }
  }

  onRemoveWarningAccounts(ids) {
    this.warningAccounts = _.filter(this.warningAccounts, (account: AccountModel) => {
      return !_.includes(ids, account.accountId);
    });
    const accounts = _.filter(this.accounts, (account: AccountModel) => {
      return !_.includes(ids, account.accountId);
    });
    this.accounts = this._resetAccounts(accounts, this._getErrorAccounts(this.accounts), this.warningAccounts);
    this.totals = this._getTotals(this.accounts);
    if (!this.accounts.length) {
      this.uploadComplete = false;
    }
  }

  onEditAccount(account: AccountModel) {
    this.editingAccount = angular.copy(account);
  }

  onCancelEditing() {
    this.editingAccount = undefined;
  }

  onSaveEditing(editingAccount) {
    _.unset(editingAccount, 'mappingErrors');
    if (this.currentNavItem === 'errors') {
      const updatedErrorAccounts = this._updateAccount(this.errorAccounts, editingAccount);
      this.accounts = this._resetAccounts(this.accounts, updatedErrorAccounts, this._getWarningAccounts(this.accounts));

      this.errorAccounts = _.filter(updatedErrorAccounts, (account :AccountModel) => {
        return account.accountId !== editingAccount.accountId;
      });
    }
    if (this.currentNavItem === 'warnings') {
      this.warningAccounts = this._updateAccount(this.warningAccounts, editingAccount);
      this.accounts = this._resetAccounts(this.accounts, this._getErrorAccounts(this.accounts), this.warningAccounts);
    } else {
      this.accounts = this._updateAccount(this.accounts, editingAccount);
    }
    this.editingAccount = undefined;
    this.totals = this._getTotals(this.accounts);
  }

  onMultipleDelete(ids) {
    if (this.currentNavItem === 'errors') {
      this.onRemoveErrorAccounts(ids);
    }
    if (this.currentNavItem === 'warnings') {
      this.onRemoveWarningAccounts(ids);
    } else {
      this.onRemoveAccounts(ids);
    }
    this.selectedIds = [];
  }

  onCancel(event) {
    return this.confirmDialogService.confirm('Cancel Upload', 'Are you sure you want to cancel the upload?', event)
      .then(() => {
        this.accounts = [];
        this.errorAccounts = [];
        this.currentNavItem = 'total';

        this.totals = {};
        this.selectedIds = [];
        this.uploadComplete = false;
        this.confirmChangeStateService.reset();
      })
      .catch(() => {
        this.$log.info('Dialog Closed');
      })
  }

  onCreate(event) {
    const accounts = _.map(angular.copy(this.accounts), (account :AccountModel) => {
      account.confirm = true;
      return account;
    });

    const serverErrorsCount = this._getWarningAccounts(accounts).length;
    this._confirmCreate(serverErrorsCount, event)
      .then(() => {
        this.bringozLoaderService.on();
        return this.batchRequestsService.createAccountsBatch(accounts, this.doUpdate);
      })
      .then((batch) => {
        this.bringozLoaderService.off();
        return this.batchDialogService.batchAccountsProgress(event, batch);
      })
      .then((errorItems) => {
        const errorRowNumbers = _.map(errorItems, 'rowNumber');
        let errorAccounts = _.chain(this.accounts)
          .filter((o) => {
            return _.includes(errorRowNumbers, o.rowNumber)
          })
          .map((filteredAccount) => {
            const errorItem = _.find(errorItems, (errorItem) => {
              return filteredAccount.rowNumber === errorItem.rowNumber;
            })
            filteredAccount.mappingErrors = _.get(errorItem, 'mappingErrors', []);
            return filteredAccount;
          })
          .value();

        errorAccounts = this._setAllAccountsIds(errorAccounts);
        this.accounts = errorAccounts;
        this.totals = this._getTotals(errorAccounts);

        if (this.currentNavItem === 'errors') {
          this.errorAccounts = this._getErrorAccounts(errorAccounts);
        }
        if (this.currentNavItem === 'warnings') {
          this.warningAccounts = this._getWarningAccounts(errorAccounts);
        }
        if (!this.accounts.length) {
          this.uploadComplete = false;
        }
      })
      .catch((error) => {
        if (error) {
          this.accountsAlerts.demandCreateManyError(error);
          this.$log.error('Failed to create batch', error);
        } else {
          this.$log.info('Dialog Closed');
        }
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });
  }

  onDownloadExample() {
    const downloadUrl = this.accountsConstants.importSampleFileLink;
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = "import-accounts-example.xls";
    document.body.appendChild(a);
    a.click();
  }

  onOpenFieldMappingPanel() {
    this.isFieldMappingOpen = true;
  }

  onCloseFieldMappingPanel() {
    this.isFieldMappingOpen = false;
  }

  onSchemaCreated(schema) {
    this.schemas = [...this.schemas, schema];
  }

  onSchemaUpdated(schema) {
    this.schemas = _.map(this.schemas, (s) => s.id === schema.id ? schema : s);
  }

  onSchemaDeleted(schemaId) {
    this.schemas = _.reject(this.schemas, (s) => s.id === schemaId);
  }

  // ******************************
  //        Private methods
  // ******************************

  private _loadSchemas(): IPromise<void> {
    // The schema endpoints answer 403 without import.schemas, and the global interceptor turns any
    // 403 into the 403 page - so users who may only import accounts must not call them.
    if (!this.IAMPermissions.hasPermission(ACCOUNTS_SCHEMA_PERMISSION)) {
      return this.$q.when();
    }

    // batchRequestsService returns native promises - $q.all adopts them so the results land inside
    // a digest. A failure must not block uploading, it only means no saved mapping can be picked.
    return this.$q.all([
      this.$q.when(this.batchRequestsService.getSchemas(ACCOUNTS_SCHEMA_ENTITY_TYPE)).catch(() => []),
      this.$q.when(this.batchRequestsService.getSchemaDefaultFields(ACCOUNTS_SCHEMA_ENTITY_TYPE)).catch(() => [])
    ])
      .then(([schemas, schemaDefaultFields]: any[]) => {
        this.schemas = schemas || [];
        this.schemaDefaultFields = schemaDefaultFields || [];
      });
  }

  private _setAllAccountsIds(accounts: AccountModel[]) {
    return _.map(accounts, (account: AccountModel) => {
      account.accountId = this.UUIDService.generate();
      return account;
    })
  }

  private _getTotals(accounts: AccountModel[]) {
    const errors = _.reduce(accounts, (sum: any, account) => {
      return sum + !_.isEmpty(account.mappingErrors);
    }, 0);

    const warnings = _.reduce(accounts, (sum: any, account) => {
      const warningList = _.filter(_.get(account, 'errors', []), (error) => {
        return error.fieldName === 'server';
      });
      return sum + warningList.length;
    }, 0);
    return {
      total: accounts.length,
      errors: errors,
      warnings: warnings
    }
  }

  private _getErrorAccounts(accounts: AccountModel[]) {
    return _.filter(accounts, (account: AccountModel) => {
      const errorList = _.filter(_.get(account, 'mappingErrors', []), (error) => {
        return error.fieldName !== 'server';
      });
      return errorList.length > 0;
    });
  }

  private _getWarningAccounts(accounts: AccountModel[]) {
    return _.filter(accounts, (account: AccountModel) => {
      return _.filter(_.get(account, 'mappingErrors', []), (error) => {
        return error.fieldName === 'server';
      }).length > 0;
    })
  }

  private _updateAccount(accounts, editingAccount) {
    return _.map(accounts, (account: AccountModel) => {
      if (account.accountId === editingAccount.accountId) {
        return editingAccount;
      }
      return account;
    });
  }

  private _resetAccounts(accounts, errorAccounts, warningAccounts) {
    const filteredAccounts = _.filter(accounts, (account: AccountModel) => {
      return !account.mappingErrors || !_.get(account, 'errors.mappingErrors');
    });
    return _.chain(filteredAccounts)
      .concat(errorAccounts, warningAccounts)
      .uniqWith(_.isEqual)
      .value();
  }

  private _confirmCreate(serverErrorsCount, event) {
    return serverErrorsCount > 0 ?
      this.confirmDialogService.confirm('Unhandled Warning',
        'There are some unhandled warnings, are you sure you want to continue?', event) :
      this.$q.all(undefined);
  }

}

angular
  .module('bringoz.admin.accounts')
  .component('uploadAccountsView', {
    bindings: {},
    template: require('./upload-accounts-view.html').default,
    controller: uploadAccountsViewCtrl
  });
